import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { 
  createRazorpayOrder, 
  openRazorpayCheckout, 
  verifyRazorpayPayment,
  type RazorpayPaymentResponse
} from '@/lib/payment';
import { getHospitalSubscription, getSubscriptionPlans, type SubscriptionPlan } from '@/lib/subscription';
import { supabase } from '@/lib/supabase';
import { calculateTax, getTaxLabel } from '@/lib/tax';

interface PaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planId: string;
  planName: string;
  amount: number;
  currency: string;
  onSuccess?: () => void;
}

export function PaymentDialog({
  open,
  onOpenChange,
  planId,
  planName,
  amount,
  currency,
  onSuccess
}: PaymentDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'init' | 'processing' | 'success' | 'error'>('init');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hospitalName, setHospitalName] = useState<string>('');

  useEffect(() => {
    if (open) {
      loadHospitalName();
    }
  }, [open]);

  const loadHospitalName = async () => {
    try {
      const subscription = await getHospitalSubscription();
      if (subscription) {
        setHospitalName(subscription.name || 'Hospital');
      }
    } catch (error) {
      console.error('Error loading hospital name:', error);
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    setStep('processing');
    setErrorMessage(null);

    try {
      // Calculate tax (amount is base amount, tax-exclusive)
      const taxCalculation = calculateTax(amount);
      
      // Step 1: Create Razorpay order with total amount (base + tax)
      const orderData = await createRazorpayOrder(planId, amount, taxCalculation.totalAmount, currency);

      if (!orderData) {
        throw new Error('Failed to create payment order');
      }

      // Step 2: Close our dialog before opening Razorpay (to avoid z-index conflicts)
      onOpenChange(false);
      
      // Small delay to ensure dialog is closed
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Step 3: Open Razorpay checkout with total amount
      await openRazorpayCheckout(
        orderData.orderId,
        orderData.key,
        taxCalculation.totalAmount,
        currency,
        planName,
        hospitalName,
        async (response: RazorpayPaymentResponse) => {
          // Payment successful - verify and activate subscription
          try {
            setStep('processing');
            const verified = await verifyRazorpayPayment(response);

            if (verified) {
              setStep('success');
              toast({
                title: 'Payment Successful',
                description: 'Your subscription has been activated successfully!',
              });

              // Refresh subscription data
              if (onSuccess) {
                setTimeout(() => {
                  onSuccess();
                  onOpenChange(false);
                  setStep('init');
                }, 2000);
              } else {
                setTimeout(() => {
                  onOpenChange(false);
                  setStep('init');
                  // Reload page to refresh subscription status
                  window.location.reload();
                }, 2000);
              }
            } else {
              throw new Error('Payment verification failed');
            }
          } catch (error: any) {
            console.error('Payment verification error:', error);
            setStep('error');
            setErrorMessage(error.message || 'Payment verification failed. Please contact support.');
            toast({
              title: 'Verification Error',
              description: error.message || 'Payment verification failed. Please contact support.',
              variant: 'destructive',
            });
          }
        },
        (error: any) => {
          // Payment failed or cancelled
          setStep('error');
          setErrorMessage(error.message || 'Payment failed or was cancelled');
          setLoading(false);
          toast({
            title: 'Payment Failed',
            description: error.message || 'Payment could not be completed.',
            variant: 'destructive',
          });
        }
      );
            } catch (error: any) {
              console.error('Payment error:', error);
              const errorMessage = error.message || 'Failed to initialize payment. Please try again.';
              setStep('error');
              setErrorMessage(errorMessage);
              setLoading(false);
              toast({
                title: 'Payment Error',
                description: errorMessage,
                variant: 'destructive',
              });
            }
  };

  const handleClose = () => {
    if (step === 'processing') {
      toast({
        title: 'Payment in Progress',
        description: 'Please complete the payment process or wait for it to finish.',
        variant: 'default',
      });
      return;
    }
    onOpenChange(false);
    setStep('init');
    setErrorMessage(null);
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Complete Payment</DialogTitle>
          <DialogDescription>
            {step === 'init' && 'You will be redirected to a secure payment page to complete your subscription.'}
            {step === 'processing' && 'Processing your payment. Please wait...'}
            {step === 'success' && 'Payment successful! Activating your subscription...'}
            {step === 'error' && 'Payment could not be completed.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {step === 'init' && (() => {
            const taxCalculation = calculateTax(amount);
            return (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Plan</span>
                  <span className="text-sm font-semibold">{planName}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Base Amount</span>
                  <span className="text-sm font-semibold text-gray-900">
                    ₹{taxCalculation.baseAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">{getTaxLabel()}</span>
                  <span className="text-sm font-semibold text-gray-900">
                    ₹{taxCalculation.taxAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Total Amount</span>
                    <span className="text-lg font-bold text-hospital-primary">
                      ₹{taxCalculation.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-700">
                    You will be redirected to Razorpay's secure payment gateway. Multiple payment options including UPI, Cards, Net Banking, and Wallets are available.
                  </p>
                </div>
              </div>

              <Button
                onClick={handlePayment}
                disabled={loading}
                className="w-full"
                size="lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Proceed to Payment'
                )}
              </Button>
            </div>
            );
          })()}

          {step === 'processing' && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-12 w-12 animate-spin text-hospital-primary mb-4" />
              <p className="text-center text-sm text-muted-foreground">
                {loading ? 'Initializing payment...' : 'Verifying payment...'}
              </p>
            </div>
          )}

          {step === 'success' && (
            <div className="flex flex-col items-center justify-center py-8">
              <CheckCircle className="h-12 w-12 text-green-600 mb-4" />
              <p className="text-center font-semibold text-lg mb-2">Payment Successful!</p>
              <p className="text-center text-sm text-muted-foreground">
                Your subscription has been activated. Redirecting...
              </p>
            </div>
          )}

          {step === 'error' && (
            <div className="space-y-4">
              <div className="flex flex-col items-center justify-center py-4">
                <XCircle className="h-12 w-12 text-red-600 mb-4" />
                <p className="text-center font-semibold text-lg mb-2">Payment Failed</p>
                {errorMessage && (
                  <p className="text-center text-sm text-muted-foreground mb-4">
                    {errorMessage}
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setStep('init');
                    setErrorMessage(null);
                    handlePayment();
                  }}
                  className="flex-1"
                >
                  Try Again
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

